import { Injectable } from '@angular/core';

import { combineQueries, filterNilValue, Order, QueryEntity } from '@datorama/akita';
import { Observable, switchMap } from 'rxjs';
import { distinctUntilChanged, map, pluck, shareReplay } from 'rxjs/operators';
import { AccountSelectModel } from '../../components/accounts-selector/account.select.model';
import { Account } from '../accounts/account.model';
import { AccountTypeEnum, Acct } from './acct.model';
import { AcctsState, AcctsStore } from './accts.store';
import { checkFilter, flattenNestedObject, InferFilter } from '../query-filter.functions';
import { get } from 'dot-prop';
import { AcctsService } from './accts.service';
import { TagsQuery } from '../../../features/new-tags/state/tags.query';
import { LegalEntity } from '../../../pages/admin/state/legal-entities/legal-entity.model';
import { AccountIssuer } from '../../../pages/admin/state/account-issuers/account-issuer.model';
import { SessionQuery } from '../../../core/state/session/session.query';
import { LegalEntityQuery } from '../../../pages/admin/state/legal-entities/legal-entity.query';

@Injectable({ providedIn: 'root' })
export class AcctsQuery extends QueryEntity<AcctsState> {
    accounts$ = this.selectAll();
    selectAvailableAccountTypes$ = this.selectAccountsByFilter$({
        accountType: {
            ne: AccountTypeEnum.BROKER
        }
    }).pipe(
        map((accounts) => {
            return Array.from(new Set(accounts.map((account) => account.accountType)));
        })
    );
    accountsAsObject$ = this.selectAll({ asObject: true });
    nonBrokerAccountsAsObject$ = this.selectAll({
        asObject: true,
        filterBy: (account) => account.accountType !== AccountTypeEnum.BROKER
    });

    isLoading$ = this.selectLoading();

    getAvailableAccountTypes = () => {
        return Array.from(new Set(this.getAll().map((account) => account.accountType)));
    };

    selectAvailableAccountIssuers$ = this.selectAccountsByFilter$({
        accountType: {
            ne: AccountTypeEnum.BROKER
        }
    }).pipe(
        map((accounts) => {
            return Array.from(
                new Set(accounts.map((account) => account.accountIssuerId || null))
            );
        }),
        shareReplay(1)
    );

    selectAvailableLegalEntities$ = this._legalEntityQuery.active$.pipe(
        switchMap((legalEntities) => {
            return this.selectAccountsByFilter$({
                accountType: {
                    ne: AccountTypeEnum.BROKER
                },
                ownerId: {
                    in: legalEntities.map((entity) => entity.ownerId)
                }
            });
        }),
        map((accounts) => {
            return Array.from(
                new Set(accounts.map((account) => account.ownerId || null))
            );
        }),
        shareReplay(1)
    );

    /** selectActiveAccounts$
     * @description select accounts (excluding broker accounts) owned by active/selected legal entities */
    selectActiveAccounts$ = this.selectAvailableLegalEntities$.pipe(
        switchMap((legalEntities) => {
            return this.selectAccountsByFilter$({
                ownerId: { in: legalEntities },
                accountType: {
                    ne: AccountTypeEnum.BROKER
                }
            });
        }),
        distinctUntilChanged(
            (previous, current) => JSON.stringify(previous) == JSON.stringify(current)
        ),
        shareReplay(1)
    );

    /*** selectAccountHolders$
     * @description select account holders (legal entities) that have accounts (excluding broker accounts) in the store
     * @returns Observable<{ [key: LegalEntity['ownerId']]: number }> - Observable of account holders with the number of accounts they have
     */
    selectAccountHolders$: Observable<{ [key: LegalEntity['ownerId']]: number }> =
        this.selectAccountsByFilter$({
            accountType: {
                ne: AccountTypeEnum.BROKER
            }
        }).pipe(
            map((accounts) => {
                const accountHolders: { [key: LegalEntity['ownerId']]: number } = {};
                for (let i = 0; i < accounts.length; i++) {
                    const account = accounts[i];
                    const accountHolder = account.ownerId;
                    // if account holder is not null and not already added...
                    if (accountHolder && !accountHolders[accountHolder]) {
                        // ...add it to account holder map and set it to 1 (number of accounts)
                        accountHolders[account.ownerId] = 1;
                    } else if (accountHolder) {
                        // ...else if account holder is already added, increment the number of accounts
                        accountHolders[account.ownerId]++;
                    }
                }
                return accountHolders;
            }),
            shareReplay(1)
        );

    constructor(
        protected store: AcctsStore,
        private _acctsService: AcctsService,
        private _tagsQuery: TagsQuery,
        private _sessionQuery: SessionQuery,
        private _legalEntityQuery: LegalEntityQuery
    ) {
        super(store);
        const isAdmin = this._sessionQuery.isAdminUser;
        // populate the store with accounts when query is created (component/view requests account data)
        // if user is admin, include inactive accounts otherwise exclude them
        this._acctsService.get(isAdmin).subscribe();
    }

    selectAccountsByFilter$(
        filters: InferFilter<Acct> = {},
        sortBy: keyof Acct | any = 'accountShortName',
        sortByOrder: Order = Order.ASC,
        limitTo?: number
    ): Observable<Acct[]> {
        return this.selectAll({
            sortBy,
            sortByOrder,
            limitTo,
            filterBy: (account) => {
                // ...flatten nested object and filter out null values
                const FILTERS = Object.entries<any>(flattenNestedObject(filters)).filter(
                    (filter) => filter[1] !== null
                );
                if (FILTERS.length > 0) {
                    return !FILTERS.find(([key, criteria]) => {
                        const value = get<string | number>(account, key);
                        return !checkFilter(value, criteria);
                    });
                } else {
                    return true;
                }
            }
        });
    }

    accountsByLegalEntityId(legalEntityId: LegalEntity['ownerId']) {
        return this.getAll({ filterBy: (account) => account.ownerId === legalEntityId });
    }
    accountsByAccountIssuerId(accountIssuerId: AccountIssuer['accountIssuerId']) {
        return this.getAll({
            filterBy: (account) => account.accountIssuerId === accountIssuerId
        });
    }

    selectAccountsByAge$(
        ids?: Array<Acct['accountId']>,
        order: Order = Order.ASC,
        limitTo?: number
    ) {
        let filters: InferFilter<Acct> = {
            accountType: {
                ne: AccountTypeEnum.BROKER
            }
        };
        if (ids && ids.length > 0) {
            filters.accountId = {
                in: ids
            };
        }
        return this.selectAccountsByFilter$(filters, 'openingDate', order, limitTo);
    }

    get selectAccountTags$() {
        return combineQueries([this.selectAll(), this._tagsQuery.allAsObject$]).pipe(
            map(([accounts, tags]) => {
                return [
                    ...new Set(
                        accounts.reduce((acc, account) => {
                            return [
                                ...acc,
                                ...(Object.keys(account.tags || {}).map(
                                    (tagId) => tags[tagId]
                                ) || [])
                            ];
                        }, [])
                    )
                ];
            })
        );
    }

    acctToAccount(acct: Acct): Account {
        return {
            value: acct.accountId,
            label: acct.accountClientAlias
        };
    }

    excludeBrokerAccounts() {
        return this.selectAll({
            filterBy: (acct) => {
                const notBrokerAccount = acct.accountType !== AccountTypeEnum.BROKER;
                return notBrokerAccount;
            }
        });
    }

    convertToAccounts(accounts: AccountSelectModel) {
        return {
            account: accounts.account ? this.acctToAccount(accounts.account) : null,
            paymentAccount: accounts.paymentAccount
                ? this.acctToAccount(accounts.paymentAccount)
                : null,
            brokerAccount: accounts.brokerAccount
                ? this.acctToAccount(accounts.brokerAccount)
                : null
        };
    }

    getAccountById = (id: number) => this.getEntity(id);

    findByAccountAlias(account: string) {
        return this.getAll({
            filterBy: (acct) => {
                return acct.accountClientAlias === account;
            }
        });
    }

    selectById$(id: Acct['accountId']) {
        return this.selectEntity(id);
    }

    isLoading(request: string): Observable<boolean> {
        return this.select('ui').pipe(pluck(request), pluck('isLoading'));
    }

    /** selectActiveIds$
     * @description - select active account ids
     * @returns Observable<Array<Acct['accountId']>>
     */
    get selectActiveIds$() {
        return this.selectActiveId().pipe(filterNilValue(), shareReplay(1)) as Observable<
            Array<Acct['accountId']>
        >;
    }
}
